/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 21/12/21
 */

import React, { ComponentType, MemoExoticComponent } from 'react';
import { JsonFormsState, OwnPropsOfJsonFormsRenderer, OwnPropsOfRenderer, StatePropsOfRenderer } from '@jsonforms/core';
import { JsonFormsStateContext, withJsonFormsContext } from '@jsonforms/react';

/**
 * Wrapper for JSONForms context chain. Maps from JSON Forms context to component props
 * @param mapStateToProps Function to convert from JSON Forms state tree to component props
 * @returns withJsonFormsProps
 */
export const createWithJsonFormsProps = <O extends OwnPropsOfRenderer, P extends StatePropsOfRenderer>(
    mapStateToProps: (state: JsonFormsState, ownProps: O) => P,
) => {
    const ctxToProps = (ctx: JsonFormsStateContext, props: O): P => {
        const state: JsonFormsState = { jsonforms: { ...ctx } };
        return mapStateToProps(state, props);
    };

    const withContextToProps = (
        Component: MemoExoticComponent<ComponentType<P>>,
    ): ComponentType<OwnPropsOfJsonFormsRenderer> =>
        (({ ctx, props }: JsonFormsStateContext & P) => {
            const layoutProps = ctxToProps(ctx, props);
            return <Component {...props} {...layoutProps} />;
        }) as ComponentType<OwnPropsOfJsonFormsRenderer>;

    return (Component: ComponentType<P>): ComponentType<O> =>
        withJsonFormsContext(withContextToProps(React.memo(Component)));
};
